import React, { useState, useEffect } from 'react';
import { Box, Button, Tooltip, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { ColumnsIcon, FilterIcon } from '../../assets/icons';
import { useResolutionScale } from '../../hooks';
import ColumnMenu from '../ColumnMenu/ColumnMenu';
import FilterMenu from '../FilterMenu/FilterMenu';
import { DARK_MODE } from '../../constants';
import { SummarizeRounded } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useMessageContextManager } from '../MessageOverlay/MessageOverlay';
import DefectIcon from '../../assets/icons/DefectIcon';
import RefreshIcon from '@mui/icons-material/Refresh';

/* -------------------- Styled Components -------------------- */

const ActionContainer = styled(Box)(() => {
	const { scaleValue } = useResolutionScale();
	return {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		marginTop: `${scaleValue(16)}px`,
		marginBottom: `${scaleValue(8)}px`,
		gap: `${scaleValue(16)}px`,
		overflowX: 'auto',
	};
});

const RightActions = styled(Box)({
	display: 'flex',
	alignItems: 'center',
	gap: '16px',
});

const StyledGradientButton = styled(Button)(({ gradient, theme }) => {
	const { scaleValue } = useResolutionScale();
	const fontSize = scaleValue(18);
	const paddingY = scaleValue(10);
	const paddingX = scaleValue(38);
	const minWidth = scaleValue(180);
	const minHeight = scaleValue(54);
	const borderRadius = scaleValue(8);

	return {
		minWidth: `${minWidth}px !important`,
		width: 'max-content !important',
		height: `${minHeight}px !important`,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: `${scaleValue(10)}px`,
		padding: `${paddingY}px ${paddingX}px`,
		fontFamily: "'JLR Emeric', sans-serif",
		fontWeight: 'bold',
		fontSize: `${fontSize}px !important`,
		lineHeight: 1.3,
		color: '#FFFFFF',
		textTransform: 'none',
		borderRadius,
		background: gradient,
		boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.25)',
		transition: 'all 0.25s ease-in-out',
		cursor: 'pointer',
		border: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'& svg': { color: '#FFFFFF', flexShrink: 0 },
		'& .MuiTypography-root': { color: '#FFFFFF' },
		'&:hover': {
			background: `linear-gradient(90deg, rgba(255,255,255,0.15), rgba(255,255,255,0.15)), ${gradient}`,
			transform: 'translateY(-1px)',
			boxShadow:
				theme.palette.mode === 'dark'
					? '0 0 10px 3px rgba(255, 255, 255, 0.1)'
					: '0 0 10px 3px rgba(0, 0, 0, 0.15)',
		},
		'&:active': {
			transform: 'translateY(0)',
			boxShadow:
				theme.palette.mode === 'dark'
					? '0 0 6px 1px rgba(255, 255, 255, 0.05)'
					: '0 0 6px 1px rgba(0, 0, 0, 0.1)',
		},
		'&.Mui-disabled': {
			background: 'linear-gradient(90deg, #9EA9A0 0%, #B8C4B0 100%)',
			color: '#FFFFFF',
			cursor: 'not-allowed !important',
			boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.25)',
			'& svg, & .MuiTypography-root': { color: '#FFFFFF' },
		},
	};
});

/* -------------------- Utility Buttons -------------------- */

export const GradientButton = ({ children, gradient, ...props }) => (
	<Tooltip title={typeof children === 'string' ? children : ''} arrow>
		<span>
			<StyledGradientButton gradient={gradient} {...props}>
				{children}
			</StyledGradientButton>
		</span>
	</Tooltip>
);

/** Same Add Defect control as Part Validation (TableActionBar) — use everywhere for consistency */
export const ADD_DEFECT_GRADIENT =
	'linear-gradient(90deg, #BD0020 0%, #FF2742 100%)';

export const AddDefectButton = ({ onClick, disabled, sx }) => (
	<GradientButton
		onClick={onClick}
		disabled={disabled}
		gradient={ADD_DEFECT_GRADIENT}
		sx={sx}
	>
		<DefectIcon />
		Add Defect
	</GradientButton>
);

export const GenealogyChip = ({
	label = 'Genealogy Pending',
	value = 0,
	success = false, // true = red, false = green
}) => {
	const { scaleValue, getResponsiveValue } = useResolutionScale();

	const ChipBox = styled(Box)(({ success }) => ({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: `${scaleValue(8)}px`,
		padding: `${scaleValue(6)}px ${scaleValue(10)}px`,
		borderRadius: `${scaleValue(12)}px`,
		minWidth: `${scaleValue(180)}px`,
		background: success
			? 'linear-gradient(90deg, #D49696 0%, #EEAEAE 100%)' // red tone
			: 'linear-gradient(90deg, #9CD496 0%, #B2EEAE 100%)', // green tone
	}));

	const ChipLabel = styled(Typography)(({ success }) => ({
		fontFamily: "'JLR Emeric', sans-serif",
		fontWeight: 600,
		fontSize: `${getResponsiveValue({
			laptop: 14,
			fhd: 15.5,
			qhd: 16.5,
			'4k': 18,
			'4k_plus': 20,
		})}px`,
		color: success ? '#A90101' : '#01A90C',
		whiteSpace: 'nowrap',
	}));

	return (
		<ChipBox success={success}>
			<ChipLabel success={success}>{label}</ChipLabel>
			<ChipLabel success={success}>{value}</ChipLabel>
		</ChipBox>
	);
};

// ------------------------- ProgressHeader -------------------------
export const ProgressHeader = ({ current, total, label }) => {
	const theme = useTheme();
	const { scaleValue } = useResolutionScale();

	const color = theme.palette.mode === DARK_MODE ? '#F8F8F8' : '#202020';
	const currentSize = scaleValue(22);
	const totalSize = scaleValue(24);
	const labelSize = scaleValue(20);

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: 1,
				height: scaleValue(36),
				px: scaleValue(1.5),
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'baseline' }}>
				<Typography
					variant="inherit"
					sx={{
						fontFamily: "'Uni Neue-Trial', sans-serif",
						fontWeight: 700,
						fontSize: `${currentSize}px`,
						lineHeight: '100%',
						color,
						letterSpacing: 0,
					}}
				>
					{current}
				</Typography>

				<Box
					component="span"
					sx={{
						fontFamily: "'JLR Emeric', sans-serif",
						fontWeight: 280,
						fontSize: `${currentSize}px`,
						lineHeight: '100%',
						color,
						mx: '2px',
						display: 'inline-block',
						letterSpacing: 0,
					}}
				>
					/
				</Box>

				<Typography
					variant="inherit"
					sx={{
						fontFamily: "'Uni Neue-Trial', sans-serif",
						fontWeight: 700,
						fontSize: `${totalSize}px`,
						lineHeight: '100%',
						color,
						display: 'inline-block',
						letterSpacing: 0,
					}}
				>
					{total}
				</Typography>
			</Box>

			<Typography
				variant="inherit"
				sx={{
					fontFamily: "'JLR Emeric', sans-serif",
					fontWeight: 600,
					fontSize: `${labelSize}px`,
					lineHeight: '100%',
					color,
					display: 'flex',
					alignItems: 'center',
				}}
			>
				{label}
			</Typography>
		</Box>
	);
};
/* -------------------- Main Action Bar -------------------- */

const TableActionBar = ({
	leftContent,
	onDefectClick,
	onFilterChange,
	onColumnChange,
	showDefactive,
	handleDefectChange,
	showRefresh = false,
	onRefresh,
	showExport = false,
	showFilter = false,
	showViewReport = false,
	onViewReportClick,
	showColumns = false,
	columns = [],
	rows = [],
	defaultColumns = [],
	rightContent,
}) => {
	const { scaleValue } = useResolutionScale();

	// Local state for menus
	const [filterAnchor, setFilterAnchor] = useState(null);
	const [columnAnchor, setColumnAnchor] = useState(null);

	// Local filters state
	const [filters, setFilters] = useState({});

	// Local columns state to make toggle changes visible immediately
	const [columnsState, setColumnsState] = useState(columns);

	useEffect(() => {
		setColumnsState(columns);
	}, [columns]);

	/* -------------------- Handlers -------------------- */

	const handleFilterChange = (newFilters) => {
		setFilters(newFilters);
		onFilterChange?.(newFilters); // propagate filters to parent
	};

	const handleColumnChange = (updatedColumns) => {
		setColumnsState(updatedColumns);
		onColumnChange?.(updatedColumns); // propagate visibility changes
	};
	const { showMessageOverlay } = useMessageContextManager();

	const handleExport = () => {
		if (!rows?.length) return;

		// ✅ Pick only visible columns
		const visibleCols = columnsState.filter((col) => !col.hidden);

		if (!visibleCols.length) {
			showMessageOverlay({
				error: true,
				contextText: 'Export Error',
				info: 'No columns are visible for export. Please select at least one column.',
			});
			return;
		}

		// ✅ Build export data
		const exportData = rows.map((row) => {
			const newRow = {};
			visibleCols.forEach((col) => {
				const key = col.field || col.key || col.title || col.label;
				const header = col.title || col.label || key;
				newRow[header] = row[key] ?? '';
			});
			return newRow;
		});

		// ✅ Generate Excel file
		const worksheet = XLSX.utils.json_to_sheet(exportData);
		const workbook = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

		const today = new Date().toISOString().slice(0, 10);
		XLSX.writeFile(workbook, `Kspec_Download_${today}.xlsx`);
	};

	/* -------------------- Render -------------------- */

	return (
		<ActionContainer>
			{/* Left Content */}
			<Box sx={{ display: 'flex', alignItems: 'center' }}>{leftContent}</Box>

			{/* Right Side Buttons */}
			<RightActions>
				{showDefactive && (
					<AddDefectButton onClick={onDefectClick} />
				)}
				{/* Refresh Button */}
				{showRefresh && (
					<GradientButton
						onClick={onRefresh}
						gradient="linear-gradient(90deg, #2196F3 0%, #21CCE1 100%)"
					>
						<RefreshIcon />
						Refresh
					</GradientButton>
				)}

				{/* Filter Button */}
				{showFilter && (
					<>
						<GradientButton
							onClick={(e) => {
								const visibleCols = columnsState.filter((col) => !col.hidden);
								if (visibleCols.length === 0) {
									showMessageOverlay({
										error: true,
										contextText: 'No Columns Visible',
										info: 'Please enable at least one column before using filters.',
									});
									return;
								}
								setFilterAnchor(e.currentTarget);
							}}
							gradient="linear-gradient(90deg, rgba(6,129,74,1) 0%)"
						>
							<FilterIcon />
							Filter
						</GradientButton>

						<FilterMenu
							open={Boolean(filterAnchor)}
							handleClose={() => setFilterAnchor(null)}
							anchorEl={filterAnchor}
							columns={columnsState}
							rows={rows}
							filters={filters}
							onFilterChange={handleFilterChange}
						/>
					</>
				)}

				{/* Columns Button */}
				{showColumns && (
					<>
						<GradientButton
							onClick={(e) => setColumnAnchor(e.currentTarget)}
							gradient="linear-gradient(90deg, rgba(236,176,64,1) 0%)"
						>
							<ColumnsIcon />
							Columns
						</GradientButton>
						<ColumnMenu
							open={Boolean(columnAnchor)}
							handleClose={() => setColumnAnchor(null)}
							anchorEl={columnAnchor}
							columns={columnsState}
							onColumnChange={handleColumnChange}
							defaultColumns={defaultColumns}
						/>
					</>
				)}

				{/* Export Button */}
				{showExport && (
					<GradientButton
						onClick={handleExport}
						gradient="linear-gradient(90deg, #435C42 0%, #619C31 100%)"
					>
						Export
					</GradientButton>
				)}

				{/* {showException && (
					<GradientButton
						onClick={onExceptionClick}
						gradient="linear-gradient(90deg, #435C42 0%, #619C31 100%)"
					>
            <ErrorRounded/>
						Exception
					</GradientButton>
				)}

				// {showTakeOut && (
				// 	<GradientButton
				// 		onClick={onTakeOutClick}
				// 		gradient="linear-gradient(90deg, #CB1D49 0%, #C71616 100%)"
				// 	>
        //     <FlightTakeoff/>
				// 		Take Out
				// 	</GradientButton>
				// )} */}

				{showViewReport && (
					<GradientButton
						onClick={onViewReportClick}
						gradient="linear-gradient(175.2deg, #064A88 11.1%, #064A88 89.38%)"
					>
						<SummarizeRounded
							sx={{
								fontSize: `${scaleValue(27)}px !important`,
							}}
						/>
						View Report
					</GradientButton>
				)}
				{rightContent}
			</RightActions>
		</ActionContainer>
	);
};

export default TableActionBar;
